import type { MasterPackagingTypeData } from "services/interface";

/** สอดคล้อง SKUModalForm — ซ่อนมิติเมื่อเลือก child มาตรฐาน (ไม่ใช่ is_other) */
export function shouldHideAutoDimensions(
  showChildDropdown: boolean,
  selectedPackaging?: MasterPackagingTypeData | null
): boolean {
  if (!showChildDropdown || !selectedPackaging) return false;
  return selectedPackaging.is_other !== true;
}

/** แสดงช่องกรอกมิติเอง — เมื่อ is_other หรือ parent ไม่มีลูก (และไม่ใช่โหมด auto-hide) */
export function shouldShowManualDimensionFields(
  packagingIncomplete: boolean,
  showChildDropdown: boolean,
  selectedPackaging?: MasterPackagingTypeData | null
): boolean {
  if (packagingIncomplete || !selectedPackaging) return false;
  return !shouldHideAutoDimensions(showChildDropdown, selectedPackaging);
}

/** แสดงช่องกว้าง–ยาว–สูง (ก่อนเลือกบรรจุภัณฑ์ หรือโหมดกรอกเอง) */
export function shouldShowDimensionInputFields(
  packagingIncomplete: boolean,
  showManualDimensions: boolean,
  hideAutoDimensions: boolean
): boolean {
  if (hideAutoDimensions) return false;
  return packagingIncomplete || showManualDimensions;
}

export function isPackagingSelectionIncomplete(
  parentPackagingTypeId?: string,
  showChildDropdown?: boolean,
  packagingTypeId?: string
): boolean {
  if (!parentPackagingTypeId) return true;
  if (showChildDropdown && !packagingTypeId) return true;
  return false;
}

export function getPackagingDimensions(
  boxWidth?: number,
  boxLength?: number,
  boxHeight?: number
) {
  return {
    width: Number(boxWidth ?? 0),
    length: Number(boxLength ?? 0),
    height: Number(boxHeight ?? 0),
  };
}

export function isDimensionSumValid(width: number, length: number, height: number): boolean {
  if (!(width > 0 && length > 0 && height > 0)) return false;
  return width + length + height <= 1000;
}

/** น้ำหนักรวม (กล่อง+สินค้า) ต้องไม่น้อยกว่าน้ำหนักสินค้าที่คำนวณได้ */
export function isBoxWeightAtLeastProductWeight(
  boxWeight: number | undefined,
  totalProductWeightGrams: number
): boolean {
  const wt = Number(boxWeight ?? 0);
  if (!(wt > 0)) return false;
  return wt >= totalProductWeightGrams;
}

export function canSearchParcelCarriers(params: {
  packagingIncomplete: boolean;
  boxWeight?: number;
  boxWidth?: number;
  boxLength?: number;
  boxHeight?: number;
  totalProductWeightGrams?: number;
}): boolean {
  if (params.packagingIncomplete) return false;
  const wt = Number(params.boxWeight ?? 0);
  if (!(wt > 0)) return false;
  if (!isBoxWeightAtLeastProductWeight(wt, params.totalProductWeightGrams ?? 0)) {
    return false;
  }
  const { width, length, height } = getPackagingDimensions(
    params.boxWidth,
    params.boxLength,
    params.boxHeight
  );
  return isDimensionSumValid(width, length, height);
}

export type PackagingSearchHint = { type: "warning" | "info"; message: string } | null;

export function getPackagingSearchHint(params: {
  packagingIncomplete: boolean;
  showChildDropdown: boolean;
  parentPackagingTypeId?: string;
  packagingTypeId?: string;
  selectedPackaging?: MasterPackagingTypeData | null;
  boxWeight?: number;
  boxWidth?: number;
  boxLength?: number;
  boxHeight?: number;
  totalProductWeightGrams?: number;
  speedHasSearched: boolean;
}): PackagingSearchHint {
  if (!params.parentPackagingTypeId) {
    return { type: "warning", message: "โปรดเลือกประเภทบรรจุภัณฑ์" };
  }
  if (params.showChildDropdown && !params.packagingTypeId) {
    return { type: "warning", message: "โปรดเลือกบรรจุภัณฑ์ย่อย" };
  }

  const hideAuto = shouldHideAutoDimensions(
    params.showChildDropdown,
    params.selectedPackaging
  );
  const wt = Number(params.boxWeight ?? 0);
  const { width, length, height } = getPackagingDimensions(
    params.boxWidth,
    params.boxLength,
    params.boxHeight
  );

  const productWt = params.totalProductWeightGrams ?? 0;
  if (wt > 0 && !isBoxWeightAtLeastProductWeight(wt, productWt)) {
    return {
      type: "warning",
      message: `น้ำหนักต้องไม่น้อยกว่าน้ำหนักสินค้า (${productWt.toLocaleString("th-TH")} กรัม)`,
    };
  }

  if (hideAuto) {
    if (wt <= 0) {
      return { type: "warning", message: "โปรดระบุน้ำหนัก (กล่อง + สินค้า)" };
    }
    if (!isDimensionSumValid(width, length, height)) {
      return {
        type: "warning",
        message: "บรรจุภัณฑ์ที่เลือกไม่มีขนาดครบ — เลือกประเภทอื่น",
      };
    }
  } else if (!(width > 0 && length > 0 && height > 0 && wt > 0)) {
    return { type: "warning", message: "โปรดระบุขนาดกล่องและน้ำหนัก (กล่อง + สินค้า)" };
  }

  if (width + length + height > 1000) {
    return { type: "warning", message: "กว้างยาวสูงรวมกันต้องไม่เกิน 1,000 ซ.ม." };
  }

  if (!params.speedHasSearched && canSearchParcelCarriers(params)) {
    return { type: "info", message: "คลิกปุ่มค้นหาบริษัทขนส่ง เพื่อแสดงค่าขนส่งที่แนะนำ" };
  }

  return null;
}

/** นำมิติจาก master ลงฟอร์ม (เช่นเดียว SKUModalForm onChange child) */
export function dimensionsFromPackaging(
  item: MasterPackagingTypeData
): { box_width?: number; box_length?: number; box_height?: number } {
  const patch: { box_width?: number; box_length?: number; box_height?: number } = {};
  if (item.width != null) patch.box_width = item.width;
  if (item.length != null) patch.box_length = item.length;
  if (item.height != null) patch.box_height = item.height;
  return patch;
}
